// Third-Party
import { Box, Stack, Typography } from "@mui/material";
import { Check, RefreshCw, X } from "lucide-react";

// Internal
import {
  getIconBadgeStyle,
  getSyncDoneButtonStyle,
  getSyncHeaderSubtitleStyle,
  getSyncHeaderTitleStyle,
  getSyncModalBoxStyle,
  getSyncPendingDotStyle,
  getSyncRetryButtonStyle,
  getSyncRowBoxStyle,
  getSyncRowErrorMessageStyle,
  getSyncRowLabelStyle,
  getSyncRowVisual,
  getSyncSpinningIconStyle,
  syncDoneCircleStyle,
  syncErrorCircleStyle,
  syncFooterStackStyle,
  syncHeaderStackStyle,
  syncOverlayStyle,
  syncRowStatusIconWrapperStyle,
  syncRowsStackStyle,
} from "../../styles/askDostStyles";
import OdooLogo from "src/assets/logo/Odoo.png";

// Types
import { IAskDostSyncProgressModalProps } from "../../types";

export const AskDostSyncProgressModal: React.FC<
  IAskDostSyncProgressModalProps
> = ({
  open,
  isFirstConnect,
  rows,
  isRunning,
  hasError,
  onRetry,
  onClose,
  palette,
  translate,
}) => {
  if (!open) return null;

  return (
    <Box sx={syncOverlayStyle} onClick={isRunning ? undefined : onClose}>
      <Box
        onClick={(e) => e.stopPropagation()}
        sx={getSyncModalBoxStyle(palette)}
      >
        <Stack
          direction="row"
          spacing={2}
          alignItems="flex-start"
          sx={syncHeaderStackStyle}
        >
          <Box sx={getIconBadgeStyle(palette, { variant: "bordered" })}>
            <img
              src={OdooLogo}
              alt="Odoo"
              width={26}
              style={{ display: "block" }}
            />
          </Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="subtitle1" sx={getSyncHeaderTitleStyle(palette)}>
              {translate(
                isFirstConnect
                  ? "connecting_to_erp_title"
                  : "syncing_with_odoo_title",
              )}
            </Typography>
            <Typography variant="caption" sx={getSyncHeaderSubtitleStyle(palette)}>
              {translate(
                isFirstConnect
                  ? "connecting_to_erp_subtitle"
                  : "syncing_with_odoo_subtitle",
              )}
            </Typography>
          </Box>
        </Stack>

        <Stack spacing={1} sx={syncRowsStackStyle}>
          {rows
            .filter((row) => {
              const isPO =
                row.key === "purchase_orders" || row.key === "purchaseOrders";
              if (isPO && row.status === "error") {
                return false;
              }
              return true;
            })
            .map((row) => {
              const visual = getSyncRowVisual(row.status, palette);
              return (
                <Box key={row.key} sx={getSyncRowBoxStyle(visual)}>
                  <Stack direction="row" alignItems="center" spacing={2}>
                    <Box sx={syncRowStatusIconWrapperStyle}>
                      {row.status === "pending" && (
                        <Box sx={getSyncPendingDotStyle(palette)} />
                      )}
                      {row.status === "busy" && (
                        <Box
                          component={RefreshCw}
                          size={16}
                          sx={getSyncSpinningIconStyle(palette)}
                        />
                      )}
                      {row.status === "done" && (
                        <Box sx={syncDoneCircleStyle}>
                          <Check
                            size={12}
                            color={palette.success.dark}
                            strokeWidth={3}
                          />
                        </Box>
                      )}
                      {row.status === "error" && (
                        <Box sx={syncErrorCircleStyle}>
                          <X
                            size={12}
                            color={palette.error.main}
                            strokeWidth={3}
                          />
                        </Box>
                      )}
                    </Box>
                    <Typography variant="body2" sx={getSyncRowLabelStyle(visual)}>
                      {row.label}
                    </Typography>
                  </Stack>
                  {row.status === "error" && (
                    <Typography
                      variant="caption"
                      sx={getSyncRowErrorMessageStyle(palette)}
                    >
                      {translate("sync_failed_message")}
                    </Typography>
                  )}
                </Box>
              );
            })}
        </Stack>

        <Stack
          direction="row"
          justifyContent="flex-end"
          spacing={1}
          sx={syncFooterStackStyle(palette)}
        >
          {hasError && (
            <Box onClick={onRetry} sx={getSyncRetryButtonStyle(palette)}>
              <RefreshCw size={14} />
              {translate("retry")}
            </Box>
          )}
          <Box
            onClick={isRunning ? undefined : onClose}
            sx={getSyncDoneButtonStyle(palette, isRunning)}
          >
            {translate(
              isRunning ? (isFirstConnect ? "connecting" : "syncing") : "done",
            )}
          </Box>
        </Stack>
      </Box>
    </Box>
  );
};